import React, { useContext, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import clsx from 'clsx';
import { useMediaQuery } from 'react-responsive';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import {
  ArrivalDepartureListResponseOutDTO,
  EntityTypeEnum,
  LovDTO,
  Pageable,
  PermissionTypesEnum,
  VisitorRecordFilterInDTO,
  VisitorRecordGroupedFilterDTO,
  VisitorRecordListInnerResponseOutDTO,
} from '@api/ringil-api.v2.tsx';
import { getBrowserTimezone } from '@utils/dateWithTz.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgPlus from '@icons/plus.svg?react';
import { useVisitorSidebar, VisitorRecord } from '@app/pages/timeslots/sidebars/VisitorSidebar.tsx';
import RingilDataTable, {
  BaseTag,
  ColumnConfig,
  FilterConfig,
  FilterType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import { ApiContext } from '@api/api.tsx';
import { Link } from 'react-router-dom';
import { isNotBlank, useLocalStorage } from '@utils/utils.tsx';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import SvgFile from '@icons/File.tsx';
import SvgUsers from '@icons/users-inverted.svg?react';
import useResponsiblePersons from '@hooks/responsible-persons/responsible-persons.tsx';
import useWorkflowStates from '@hooks/useWorkFlowStates/useWorkflowStates.tsx';
import { WorkflowStateButton } from '@components/workflow/WorkflowStateButton.tsx';
import MoreButton from '@components/RingilComponents/MoreButton/MoreButton.tsx';
import ExpandableLabelWrapper from '@components/RingilDataTable/ExpandableLabelWrapper.tsx';
import SecuredArea from '@components/security/SecuredArea';
import { useSecuredFlag } from '@components/security/useSecuredFlag';

interface ComponentProps {
}

export interface VisitorEntry {
  personName: string;
  personTypeAndId?: string;
  carPlate?: string;
  entryDateTime: string;
  exitDateTime?: string;
  note?: string;
}

const Visits: React.FC<ComponentProps> = () => {
  const { tr } = useTranslationLgs();
  const { hasPermission } = useContext(AuthenticatedUserContext);
  const {
    getVisitorRecords,
    getLocationsWithEnabledVisitor,
    performTransitionOnEntity,
    getVisitorRecord,
    archiveVisitorRecord,
    getUserVisitorRecordFilters,
    saveUserVisitorRecordFilters,
    deleteUserFilter,
  } = useContext(ApiContext);
  const navigation = useNavigation();

  const [expandedInfoTags, setExpandedInfoTags] = useLocalStorage('VISITORS_EXPANDED_INFO_TAGS', false);

  const { VisitorSidebar, setVisible: setVisibleVisitorSidebar, visible: visibleVisitorSidebar } = useVisitorSidebar();
  const responsiblePersons = useResponsiblePersons();
  const workflowStates = useWorkflowStates(EntityTypeEnum.VISITOR_RECORD);
  const [locations, setLocations] = useState<LovDTO[]>([]);
  const [groupedFilters, setGroupedFIlters] = useState<VisitorRecordGroupedFilterDTO | null>(null);
  const [selectedItem, setSelectedItem] = useState<VisitorRecord | null>(null);

  useEffect(() => {
    getLocationsWithEnabledVisitor(
      data => setLocations(data),
    );
  }, []);

  const isMobile = useMediaQuery({ maxWidth: 1024 });
  const visitorsListRwFlag = useSecuredFlag('visitors-list-rw', {
    kind: 'action',
    description: 'Create / manage visits in the list',
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_VISITORS_WRITE],
  });
  const editEnabled = visitorsListRwFlag.granted;
  const editStatusEnabled = hasPermission([PermissionTypesEnum.PERMISSION_TIMESLOT_VISITORS_WRITE, PermissionTypesEnum.PERMISSION_TIMESLOT_CHANGE_STATUS]);

  const renderIdColumn = (data: VisitorRecordListInnerResponseOutDTO) => {
    return (

      <div className={'flex flex-col items-start gap-2'}>
        <Link
          to={navigation.createNavigationLink(navigation.urlFunctions.createVisitDetail(data.id))}
        >
            <span
              className={'text-[16px] font-normal leading-normal tracking-[0.24px] text-content-brand font-dm-sans'}
            >
              {data.applicationId}
            </span>
        </Link>
        <span
          className={'text-[12px] font-normal leading-normal tracking-[0.24px] text-content-secondary font-dm-sans'}>
            {data.responsiblePersonName}
          </span>
        <span
          className={'text-[12px] font-normal leading-normal tracking-[0.24px] text-content-secondary font-dm-sans'}>
            {data.locationName}
        </span>
        <ExpandableLabelWrapper
          labels={data.labels}
          isExpanded={expandedInfoTags}
          onToggle={toggleInfoExpansion}
          />
      </div>
    );
  };

  const renderStatusColumn = (data: VisitorRecordListInnerResponseOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <WorkflowStateButton
          state={data.status}
          entityId={data.id}
          entityType={EntityTypeEnum.VISITOR_RECORD}
          translationKeyPrefix={'VisitorState.'}
          disabled={!editStatusEnabled}
          onStateChange={(stateId) => {
            performTransitionOnEntity(
              data.id,
              EntityTypeEnum.VISITOR_RECORD,
              { transitionToName: stateId },
              () => {
                tableRef.current?.reloadData();
              },
            )
          }}
        />
      </div>
    );
  };

  const renderMainContactColumn = (data: VisitorRecordListInnerResponseOutDTO) => {
    return (

      <div className={'flex flex-col items-start gap-2'}>
        <span
          className={'text-[16px] font-medium not-italic leading-normal tracking-[-0.16px] text-content-primary font-dm-sans'}>
          {data.visitorName}
        </span>
        {data.visitorId && (
          <span
            className={'text-[14px] font-normal leading-normal text-content-secondary font-dm-sans'}>
            {data.visitorId}
          </span>
        )}
        {data.visitorCompany && (
          <span
            className={'text-[14px] font-normal leading-normal text-content-secondary font-dm-sans'}>
            {data.visitorCompany}
          </span>
        )}
        {data.carPlate && (
          <span
            className={'text-[14px] font-normal leading-normal text-content-secondary font-dm-sans'}>
            {tr('Visitors.dataCarPlate', 'Car plate: {{carPlate}}', { carPlate: data.carPlate })}
          </span>
        )}

      </div>
    );
  };

  const renderArrivalColumn = (data: VisitorRecordListInnerResponseOutDTO) => {
    return renderTimeColumn(data?.arrival);
  };

  const renderDepartureColumn = (data: VisitorRecordListInnerResponseOutDTO) => {
    return renderTimeColumn(data?.departure);
  };

  const renderTimeColumn = (data?: ArrivalDepartureListResponseOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        {data?.time && (
          <span
            className={'text-[14px] font-normal leading-normal text-content-primary font-dm-sans'}>
            {data?.time}
          </span>
        )}

        {data?.date && (
          <span
            className={'text-[12px] font-normal not-italic leading-normal tracking-[0.24px] text-content-tertiary font-dm-sans'}>
            {data?.date}
          </span>
        )}

        {data?.expected && (
          <span
            className={'text-[12px] font-normal not-italic leading-normal tracking-[0.24px] text-content-primary font-dm-sans'}>
            {tr('Visitors.expected', 'expected')}
          </span>
        )}
      </div>
    );
  };

  const toggleInfoExpansion = () => {
    setExpandedInfoTags(prev => !prev);
  };

  const handleTagClick = () => {
    toggleInfoExpansion();
  };

  const renderAdditionalInfoColumn = (data: VisitorRecordListInnerResponseOutDTO) => {
    const additionalContactsString = data.additionalContacts
      .map(contact => `${contact.name} - ${contact.visitorId}`)
      .join(', ');

    return (
      <>
        {isNotBlank(data.note) && (
          <BaseTag id={`note_${data.applicationId}`} clickable={true} onClick={handleTagClick}
                   isExpanded={expandedInfoTags} hasContent={isNotBlank(data.note)}>
            {!expandedInfoTags && (
              <CustomTooltip targetSelector={`#note_${data.applicationId}`}
                             content={`${tr('Visitors.noteTagLabel', 'Note: ')}${data.note}`} />
            )}
            <SvgFile />
            {expandedInfoTags && (
              <span>
                  <span className="tag-label">{tr('Visitors.noteTagLabel', 'Note: ')}</span>
                  <span className="tag-text">{data.note}</span>
                </span>
            )}
          </BaseTag>
        )}
        {data.additionalContacts && data.additionalContacts.length > 0 && (
          <BaseTag
            id={`additionUsers_${data.applicationId}`}
            clickable={true}
            onClick={handleTagClick}
            isExpanded={expandedInfoTags}
            hasContent={data.additionalContacts && data.additionalContacts.length > 0}
          >
            {!expandedInfoTags && (
              <CustomTooltip targetSelector={`#additionUsers_${data.applicationId}`}
                             content={`${tr('Visitors.additionalContactsTagLabel', 'Additional visitors: ')}${additionalContactsString}`} />
            )}
            <SvgUsers />
            {!expandedInfoTags && (
              <span>+{data.additionalContacts.length}</span>
            )}
            {expandedInfoTags && (
              <span>
                <span className="tag-label">+{data.additionalContacts.length} </span>
                <span className="tag-label">{tr('Visitors.additionalContactsTagLabel', 'Additional visitors:')} </span>
                <span className="tag-text">{additionalContactsString}</span>
              </span>
            )}
          </BaseTag>
        )}
      </>
    );
  };

  const renderActionColumn = (data: VisitorRecordListInnerResponseOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <MoreButton
          buttonMode={'table'}
          sections={[
            {
              items: [
                {
                  id: 'duplicate',
                  label: tr('Visitors.moreActionDuplicate', 'Duplicate'),
                  // The row keeps its menu without edit rights, with the actions greyed out — the old
                  // button took a `disabled` prop and did the same.
                  disabled: !editEnabled,
                  command: () => {
                    getVisitorRecord(data.id, (responseData) => {
                      setSelectedItem({
                        duplicate: true,
                        record: responseData,
                      });
                      setVisibleVisitorSidebar(true);
                    });
                  },
                },
                {
                  id: 'archive',
                  label: tr('Visitors.moreActionArchive', 'Archive'),
                  disabled: !editEnabled,
                  command: () => {
                    archiveVisitorRecord(
                      data.id,
                      { isArchived: true },
                      () => {
                        tableRef.current?.reloadData();
                      },
                    );
                  },
                },
              ],
            },
          ]}
        />
      </div>
    );
  };

  const columnsConfig: ColumnConfig<VisitorRecordListInnerResponseOutDTO>[] = [
    {
      id: 'applicationId',
      header: 'Visitors.columnHeaderApplicationId',
      headerDefault: 'Visit',
      width: '15%',
      sortable: true,
      field: 'applicationId',
      order: 1,
      render: renderIdColumn,
    },
    {
      id: 'status',
      header: 'Visitors.columnHeaderStatus',
      headerDefault: 'Status',
      width: '15%',
      sortable: false,
      order: 2,
      render: renderStatusColumn,
    },
    {
      id: 'mainContact',
      header: 'Visitors.columnHeaderMainContact',
      headerDefault: 'MainContact',
      width: '20%',
      sortable: false,
      order: 3,
      render: renderMainContactColumn,
    },
    {
      id: 'arrival',
      header: 'Visitors.columnHeaderArrival',
      headerDefault: 'Arrival',
      width: '20%',
      sortable: true,
      field: 'arrival.date',
      order: 4,
      render: renderArrivalColumn,
    },
    {
      id: 'departure',
      header: 'Visitors.columnHeaderDeparture',
      headerDefault: 'Departure',
      width: '20%',
      sortable: true,
      field: 'departure.date',
      order: 5,
      render: renderDepartureColumn,
    },
    {
      id: 'additionalInfo',
      header: 'Visitors.columnHeaderAdditionalInfo',
      headerDefault: 'Addition info',
      width: '20%',
      sortable: true,
      order: 6,
      render: renderAdditionalInfoColumn,
    },
    {
      id: 'actions',
      width: '20%',
      sortable: false,
      order: 7,
      render: renderActionColumn,
    },
  ];

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr(`Visitors.filterVisitorName`, `Visitor name`),
      value: ['visitorName'],
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr(`Visitors.filterLocation`, `Location`),
      value: ['locationIds'],
      options: (groupedFilters?.locations ?? locations).map(location => ({
        label: location.name,
        value: location.id,
        active: location.active,
      })),
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr(`Visitors.filterState`, `State`),
      value: ['statuses'],
      options: (groupedFilters?.statuses ?? workflowStates).map(value => ({
        label: tr(`VisitorState.${value}`, value),
        value,
        active: true,
      })),
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr(`Visitors.filterResponsiblePerson`, `Responsible person`),
      value: ['responsiblePersonIds'],
      options: (groupedFilters?.responsiblePersons ?? responsiblePersons).map(person => ({
        label: person.name,
        value: person.id,
        active: person.active,
      })),
    },
    {
      filterType: FilterType.Calendar,
      placeholder: tr(`Visitors.filterArrival`, `Arrival`),
      value: ['arrivalDateFrom', 'arrivalDateTo'],
    },
    {
      filterType: FilterType.Calendar,
      placeholder: tr(`Visitors.filterDeparture`, `Departure`),
      value: ['departureDateFrom', 'departureDateTo'],
    },
    {
      filterType: FilterType.Checkbox,
      placeholder: tr(`Visitors.filterArchived`, `Archived`),
      value: ['isArchived'],
    },
  ];

  const loadDataApi = (filter: VisitorRecordFilterInDTO, pageable: Pageable, onSuccess: (pagedData: PagedData<VisitorRecordListInnerResponseOutDTO>) => void) => {
    const filterWithTimezone = {
      ...filter,
      userTimezoneId: getBrowserTimezone(),
    };
    getVisitorRecords(
      pageable,
      filterWithTimezone,
      data => {
        setGroupedFIlters(data.groupedFilters);
        onSuccess(data.page);
      },
    );
  };

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  return (
    <>
      <div className={clsx('font-[\'DM Sans\']', isMobile && '-mx-4 px-8 pt-8')}>
        <BreadCrumbComponent items={[{ label: tr(`Visitors.visitorsTitle`, `Visitors`) }]} />
        <div className="flex items-center mb-6">
          <COHeadline title={tr(`Visitors.visitorsTitle`, `Visitors`)} variant={'h1'} />
          <SecuredArea id="visitors-list-create" visible={visitorsListRwFlag} label="New visit button" description="Button that creates a new visit">
            <div className="flex items-center">
              <RingilButton
                label={tr(`Visitors.newVisit`, `New visit`)}
                icon={<SvgPlus />}
                mode={'text-only'}
                onClick={() => setVisibleVisitorSidebar(true)}
              />
            </div>
          </SecuredArea>
        </div>
      </div>

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnsConfig as ColumnConfig<unknown>[]}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'visits_list'}
        getSavedFilters={getUserVisitorRecordFilters}
        saveFilters={saveUserVisitorRecordFilters}
        deleteSavedFilter={deleteUserFilter}
        normalizeFilter={filter => ({ ...filter, userTimezoneId: getBrowserTimezone() })}
      />


      {visibleVisitorSidebar && (
        <VisitorSidebar
          visible={visibleVisitorSidebar}
          setVisible={setVisibleVisitorSidebar}
          onContinue={() => {
            setSelectedItem(null);
            tableRef.current?.reloadData();
          }}
          item={selectedItem}
        />
      )}
    </>
  );
};

export default Visits;
